import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { User } from '@/types'
import { parseStoredJson, readStorage, removeStorage, writeStorage } from '@/utils/storage'
import { logger } from '@/utils/logger'

const ACCOUNTS_KEY = 'savedAccounts'
const ACTIVE_ACCOUNT_KEY = 'activeAccountId'

interface SavedAccount {
  id: string
  userId: number
  username: string
  realName: string
  role: string
  roleName: string
  phone: string
  avatar?: string
  tenantId?: number
  tenantName?: string
  accessToken: string
  refreshToken: string
  lastActiveAt: string
}

function generateAccountId(user: User): string {
  return `${user.tenantId ?? 0}:${user.id}`
}

function loadSavedAccounts(): SavedAccount[] {
  return parseStoredJson<SavedAccount[]>(ACCOUNTS_KEY) ?? []
}

function persistAccounts(accounts: SavedAccount[]): void {
  writeStorage(ACCOUNTS_KEY, JSON.stringify(accounts))
}

function loadActiveAccountId(): string | null {
  return readStorage(ACTIVE_ACCOUNT_KEY)
}

function persistActiveAccountId(id: string | null): void {
  if (id) {
    writeStorage(ACTIVE_ACCOUNT_KEY, id)
  } else {
    removeStorage(ACTIVE_ACCOUNT_KEY)
  }
}

export const useAccountsStore = defineStore('accounts', () => {
  const accounts = ref<SavedAccount[]>(loadSavedAccounts())
  const activeAccountId = ref<string | null>(loadActiveAccountId())

  const activeAccount = computed(() => {
    if (!activeAccountId.value) return null
    return accounts.value.find((a) => a.id === activeAccountId.value) ?? null
  })

  const hasMultipleAccounts = computed(() => accounts.value.length > 1)

  const otherAccounts = computed(() => {
    if (!activeAccountId.value) return accounts.value
    return accounts.value.filter((a) => a.id !== activeAccountId.value)
  })

  function addAccount(user: User, accessToken: string, refreshTokenValue: string): void {
    persistAccountRecord(user, accessToken, refreshTokenValue)
    activeAccountId.value = generateAccountId(user)
    persistAccounts(accounts.value)
    persistActiveAccountId(activeAccountId.value)
  }

  /**
   * 添加账号但不切换"当前活跃账号"指针。
   * 用于"添加账号"弹窗：用户只想把账号挂到本机列表里，不希望打断当前会话。
   * 若添加的就是当前活跃账号（极少见，例如已登录后再次添加同一账号），仍会刷新该账号的 token。
   */
  function addAccountWithoutActivating(user: User, accessToken: string, refreshTokenValue: string): void {
    persistAccountRecord(user, accessToken, refreshTokenValue)
    persistAccounts(accounts.value)
    // 不修改 activeAccountId
  }

  function persistAccountRecord(user: User, accessToken: string, refreshTokenValue: string): void {
    const id = generateAccountId(user)
    const now = new Date().toISOString()

    const existing = accounts.value.find((a) => a.id === id)
    if (existing) {
      existing.username = user.username
      existing.realName = user.realName ?? user.username
      existing.role = user.role
      existing.roleName = user.roleName ?? user.role
      existing.phone = user.phone
      existing.avatar = user.avatar
      existing.tenantId = user.tenantId
      existing.tenantName = user.tenantName
      existing.accessToken = accessToken
      existing.refreshToken = refreshTokenValue
      existing.lastActiveAt = now
    } else {
      accounts.value.push({
        id,
        userId: user.id,
        username: user.username,
        realName: user.realName ?? user.username,
        role: user.role,
        roleName: user.roleName ?? user.role,
        phone: user.phone,
        avatar: user.avatar,
        tenantId: user.tenantId,
        tenantName: user.tenantName,
        accessToken,
        refreshToken: refreshTokenValue,
        lastActiveAt: now
      })
    }
  }

  function removeAccount(accountId: string): void {
    accounts.value = accounts.value.filter((a) => a.id !== accountId)
    persistAccounts(accounts.value)

    if (activeAccountId.value === accountId) {
      if (accounts.value.length > 0) {
        activeAccountId.value = accounts.value[0].id
        persistActiveAccountId(accounts.value[0].id)
      } else {
        activeAccountId.value = null
        persistActiveAccountId(null)
      }
    }
  }

  function switchToAccount(accountId: string): SavedAccount | null {
    const account = accounts.value.find((a) => a.id === accountId)
    if (!account) {
      logger.warn('[Accounts Store] 未找到账号:', accountId)
      return null
    }

    account.lastActiveAt = new Date().toISOString()
    activeAccountId.value = accountId
    persistAccounts(accounts.value)
    persistActiveAccountId(accountId)

    return account
  }

  function updateActiveTokens(accessToken: string, refreshTokenValue: string): void {
    if (!activeAccountId.value) return
    const account = accounts.value.find((a) => a.id === activeAccountId.value)
    if (account) {
      account.accessToken = accessToken
      account.refreshToken = refreshTokenValue
      persistAccounts(accounts.value)
    }
  }

  function clearAll(): void {
    accounts.value = []
    activeAccountId.value = null
    removeStorage(ACCOUNTS_KEY)
    removeStorage(ACTIVE_ACCOUNT_KEY)
  }

  return {
    accounts,
    activeAccountId,
    activeAccount,
    hasMultipleAccounts,
    otherAccounts,
    addAccount,
    addAccountWithoutActivating,
    removeAccount,
    switchToAccount,
    updateActiveTokens,
    clearAll
  }
})
